import * as jsvat from '@accountable/jsvat';
import dayjs from 'dayjs';
import useGetCurrentCustomerData from 'hooks/Authentication/useGetCurrentCustomerData';
import useFeatures from 'hooks/useFeatures';
import { useTranslation } from 'react-i18next';
import type { IExpenseFormValuesV2 } from 'types/expenses.types';
import { ExpenseCategoryType, ExpensePaymentStatusType, ExpenseVATRegime, TripType, TripTypeIds, TripVehicleType } from 'types/expenses.types';
import { CurrenciesEnum } from 'types/global.types';
import type { IUser } from 'types/users.types';
import { AccountTypeEnum, AvailableCountries, VATTypeEnum } from 'types/users.types';
import {
  CATEGORY_BANK_CHARGES,
  CATEGORY_DIRECTORS_COMPENSATION,
  CATEGORY_HEALTH_INSURANCE,
  CATEGORY_INTEREST_AND_BANK_CHARGES,
  CATEGORY_IT_SERVICE,
  CATEGORY_LUNCH_VOUCHERS,
  CATEGORY_LUNCH_VOUCHERS_ADMIN,
  CATEGORY_OTHER_PERSONNEL_FEES,
  CATEGORY_RENT,
  CATEGORY_TAXES_AND_INSURANCE_PRIVATE_INSURANCE,
  CATEGORY_TAXES_AND_INSURANCE_VAT_PAYMENT,
  CATEGORY_TRAVELS_FLIGHTS,
  CATEGORY_TRAVEL_FOR_EMPLOYEE_FLIGHTS,
  CATEGORY_TRAVEL_FOR_SELF_EMPLOYED_FLIGHTS,
  CATEGORY_TRAVEL_LOCAL_PUBLIC_TRANSPORTATION,
  CATEGORY_WORKERS_COMPENSATION,
  CATEGORY_WORKPLACE_RENOVATION,
  CATEGORY_WORKPLACE_RENT_HOMELUMPSUM,
  CATEGORY_WORKPLACE_RENT_HOME_OFFICE_SECONDARY,
  SELF_EMPLOYED_CAR_PRIVATE_USE,
  vehiclesMonthlyLeasingCategories,
  workFromHomeCategories,
} from 'utils/categories';
import { isExpert, isWeb } from 'utils/constants';
import getFieldState from 'utils/getFieldState';
import { getShortAccountType } from 'utils/helpers';
import { isMultipleOf2, round } from 'utils/math';
import {
  doesTenDayRuleApplies,
  getVATRegimesStatus,
  isBusinessCar,
  isTaxSupplierName,
  isUtilityVehicle,
  isVehicleFineCategory,
  useVATRatesGetter,
} from './utils';

const flightsCategories = [CATEGORY_TRAVEL_FOR_SELF_EMPLOYED_FLIGHTS, CATEGORY_TRAVEL_FOR_EMPLOYEE_FLIGHTS, CATEGORY_TRAVELS_FLIGHTS];

const today = dayjs();

const mixableVATRegimes = [ExpenseVATRegime.exempt_item, ExpenseVATRegime.local_reverse_charge];

const useFormMeta = () => {
  const customer = useGetCurrentCustomerData().data as IUser;

  const { t } = useTranslation();

  const { country: customerCountry, accountType, VATNumber: userVATNumber } = customer as IUser;

  const isCustomerBE = customerCountry === AvailableCountries.belgium;
  const isCustomerDE = customerCountry === AvailableCountries.germany;

  const isCustomerOnePersonCompany = accountType === 'one_person_company';

  const deductibleTaxCategories = useFeatures('expenses.deductible_tax_categories');
  const vehicleCategoryIds = useFeatures('expenses.vehicleCategoryIds');
  const isAmortizationFn = useFeatures('expenses.is_amortization');

  const maxVATRate = useFeatures('expenses.max_vat_rate') as number;

  const importVATCategory = useFeatures('expenses.import_vat_category');
  const importFeesCategory = useFeatures('expenses.import_fees_category');
  const importFeesCategoryPlaceholder = useFeatures('expenses.import_fees_category_placeholder');

  const getVATRates = useVATRatesGetter();

  const isCustomerNaturalPerson = isCustomerDE || getShortAccountType(customer.accountType) === AccountTypeEnum.natural_person;

  const getAllowedVATRates = (date: string | undefined) => {
    return getVATRates({ expenseDate: date });
  };

  const getFormMeta = (formValues: IExpenseFormValuesV2) => {
    // test private cars
    const germanPrivateCarItem = (formValues.items || []).find((item) => item.category?.id === SELF_EMPLOYED_CAR_PRIVATE_USE);

    const isGermanPrivateCar = !!germanPrivateCarItem;

    const firstItem = formValues.items?.[0];

    const isImportVATForm = firstItem?.category?.id === importVATCategory;

    const isCustomForm = isImportVATForm || isGermanPrivateCar;

    const VATType = formValues.user?.VATType;

    const isCustomerSubjectToVAT = VATType === VATTypeEnum.subjectToVAT;

    const isDeductible = !!formValues.items?.some((item) => deductibleTaxCategories.includes(item.category?.id));

    const hasAnyServiceItems = !!formValues.items?.some((item) => item.category?.type === ExpenseCategoryType.SERVICE);

    const isPaid = formValues.payments?.status?.type === ExpensePaymentStatusType.paid;
    const isUnpaid = formValues.payments?.status?.type === ExpensePaymentStatusType.unpaid;
    const isPending = formValues.payments?.status?.type === ExpensePaymentStatusType.pending;

    const isCreditNote = !!formValues.isCreditNote;

    const masterVATRegime =
      formValues.items?.find((item) => {
        const allowedRegimes = getVATRegimesStatus({
          vatRate: item.VATRate,
          isInvoice: !!formValues.isInvoice,
          isSubjectToVAT: isCustomerSubjectToVAT,
          categoryType: item.category?.type,
          doesCategoryAllowVAT: item.category?.allowsVAT,
          isCustomForm,
          categoryId: item.category?.id,
          country: customerCountry,
        }).allowed;
        return item.VATRegime != null && !mixableVATRegimes.includes(item.VATRegime) && allowedRegimes.includes(item.VATRegime);
      })?.VATRegime || null;

    const hasManyItemsWithMasterVATRegime = masterVATRegime != null && formValues.items?.filter((item) => item.VATRegime === masterVATRegime).length > 1;

    const isInvoiceField = getFieldState({
      name: 'isInvoice',
      value: () => !!formValues.isInvoice,
      isHidden: isCustomerDE || !isCustomerSubjectToVAT || isDeductible,
      suggestedChanges: ({ name }) => {
        if (!isCustomerSubjectToVAT) {
          const _isInvoice = !!formValues.items?.some(
            (item) =>
              item.VATRegime === ExpenseVATRegime.eu_reverse_charge ||
              (item.VATRegime === ExpenseVATRegime.extra_eu_reverse_charge && item.category?.type === ExpenseCategoryType.SERVICE),
          );
          return [{ name, value: _isInvoice }];
        }

        if (isCustomerBE) {
          if (isDeductible) return [{ name, value: false }];

          const value = formValues.isInvoice;
          if (value == null) return [{ name, value: false }];
        }

        if (isCustomerDE) {
          if (isDeductible) return [{ name, value: true }];

          const hasExtraGoods = !!formValues.items?.some(
            (item) => item.VATRegime === ExpenseVATRegime.extra_eu_reverse_charge && item.category?.type === ExpenseCategoryType.GOOD,
          );

          const hasExtraServices = !!formValues.items?.some(
            (item) => item.VATRegime === ExpenseVATRegime.extra_eu_reverse_charge && item.category?.type === ExpenseCategoryType.SERVICE,
          );

          const _isTicket = hasExtraGoods && !hasExtraServices;

          return [{ name, value: !_isTicket }];
        }
      },
    });

    const supplierField = getFieldState({
      name: 'supplier',
      value: () => formValues.supplier,
      isHidden: isGermanPrivateCar,
      suggestedChanges: ({ name }) => {
        if (isGermanPrivateCar) {
          return [
            {
              name,
              value: {
                name: germanPrivateCarItem.tripType && t(`expense.form.destination.${TripTypeIds[germanPrivateCarItem.tripType]}`),
              },
            },
          ];
        }
      },
    });

    const transactionsField = getFieldState({
      name: 'payments.transactions',
      value: () => formValues.payments?.transactions || [],
      isApplicable: !isGermanPrivateCar && (isPaid || isPending),
      suggestedChanges: ({ isApplicable, name }) => {
        if (!isApplicable) return [{ name, value: [] }];
      },
    });

    const paidByCashField = getFieldState({
      name: 'payments.cash',
      value: () => formValues.payments?.cash,
      isApplicable: !isGermanPrivateCar && (isPaid || isPending),
      suggestedChanges: ({ isApplicable, name }) => {
        if (!isApplicable) return [{ name, value: false }];
      },
    });

    const paidByOtherAccountsField = getFieldState({
      name: 'payments.other_accounts',
      value: () => formValues.payments?.other_accounts,
      isApplicable: !isGermanPrivateCar && (isPaid || isPending),
      suggestedChanges: ({ isApplicable, name }) => {
        if (!isApplicable) return [{ name, value: false }];
      },
    });

    const paymentStatusField = getFieldState({
      name: 'payments.status.type',
      value: () => formValues.payments?.status?.type,
      isApplicable: !isGermanPrivateCar,
      suggestedChanges: (self) => {
        const { name, isApplicable } = self;
        if (!isApplicable) {
          return [{ name, value: ExpensePaymentStatusType.paid }];
        }
        const value = self.value(self);

        if (value === ExpensePaymentStatusType.unpaid) return;

        const transactions = transactionsField.value;

        const hasPendingTransaction = transactions.some((transaction) => transaction.status === ExpensePaymentStatusType.pending);

        if (hasPendingTransaction) {
          return [{ name, value: ExpensePaymentStatusType.pending }];
        }

        if (value === ExpensePaymentStatusType.pending) return [{ name, value: ExpensePaymentStatusType.paid }];
      },
    });

    const paymentDateField = getFieldState({
      name: 'payments.status.payment_date',
      value: () => formValues.payments?.status?.payment_date,
      isApplicable: isCustomerDE && !isGermanPrivateCar && isPaid,
      suggestedChanges: ({ isApplicable, name }) => {
        if (!isApplicable && isGermanPrivateCar) {
          return [{ name, value: formValues.expenseDate || null }];
        }
      },
    });

    const dueDateField = getFieldState({
      name: 'payments.status.due_date',
      value: () => formValues.payments?.status?.due_date,
      isApplicable: !isGermanPrivateCar && isUnpaid,
    });

    const periodField = getFieldState({
      name: 'period',
      value: () => formValues.period,
      isApplicable: isCustomerSubjectToVAT && (isCustomerBE || isPaid),
    });

    const taxPeriodField = getFieldState({
      name: 'taxPeriod',
      value: () => formValues.taxPeriod,
      isHidden: isCustomerDE,
      isApplicable: isDeductible,
    });

    const currencyField = getFieldState({
      name: 'currency',
      value: () => formValues.currency,
      isApplicable: !isGermanPrivateCar,
      suggestedChanges: ({ isApplicable, name }) => {
        const value = formValues.currency;
        if (!isApplicable || !value) {
          return [{ name, value: CurrenciesEnum.EUR }];
        }
      },
    });

    const currencyRateField = getFieldState({
      name: 'currencyRate',
      value: () => formValues.currencyRate,
      isApplicable: !!currencyField.value && currencyField.value !== CurrenciesEnum.EUR,
    });

    const hasLinkedPayments = !!(formValues.payments?.transactions?.length || formValues.payments?.cash || formValues.payments?.other_accounts);

    const isSuggestedTransactionActiveField = getFieldState({
      name: 'isSuggestedTransactionActive',
      value: () => formValues.isSuggestedTransactionActive,
      isApplicable: isUnpaid || (isPaid && !hasLinkedPayments),
      suggestedChanges: ({ isApplicable, name }) => {
        if (!isApplicable) return [{ name, value: false }];
      },
    });

    const isAL1guess = !!formValues.guessed_data?.al_version_id?.includes('AL1');

    const shouldShowTenDayRuleWarning = doesTenDayRuleApplies(
      formValues.expenseDate,
      formValues.items?.map((item) => item.category?.id),
    );

    // user classify the expense as invoice while ocr didn't get the user vat number from the file
    const shouldShowIsInvoiceWarning =
      !isAL1guess &&
      !isInvoiceField.isHidden &&
      isCustomerBE &&
      !!userVATNumber &&
      isInvoiceField.value &&
      (formValues.guessed_data?.is_invoice === null || formValues.guessed_data?.is_invoice === false);

    // user classify the expense as ticket while ocr got the user vat number from the file
    const shouShowIsTicketWarning =
      !isAL1guess && !isInvoiceField.isHidden && isCustomerBE && !!userVATNumber && !isInvoiceField.value && !!formValues.guessed_data?.is_invoice;

    const shouldShowCalcRows = !isGermanPrivateCar && !isDeductible && isInvoiceField.value && !!formValues.items?.length && formValues.items.length > 1;

    const shouldShowNewItemBtn = !isGermanPrivateCar && !isDeductible;

    const shouldShowExpenseDateWarning = isCustomerBE && !!formValues.expenseDate && dayjs(formValues.expenseDate).isAfter(today, 'day');

    const shouldShowPaymentDateWarning =
      isCustomerDE && !!formValues.payments.status?.payment_date && dayjs(formValues.payments.status?.payment_date).isAfter(today, 'day');

    // const shouldShowExpenseDateHelper = isCustomerDE;

    const isTaxSupplier = isTaxSupplierName(formValues.supplier?.name);
    const shouldShowTaxSupplierWarningForCompany = isCustomerBE && isCustomerOnePersonCompany && isTaxSupplier;
    const shouldShowTaxSupplierWarningForNaturalPerson = isCustomerBE && isCustomerNaturalPerson && isTaxSupplier;

    const guessed = formValues.guessed_data;

    const isGermanVATGuessedAsNotRecoverable =
      !!guessed &&
      !!guessed._id &&
      isCustomerDE &&
      (guessed.supplier?.address?.country !== AvailableCountries.germany ||
        !jsvat.checkVAT(guessed.supplier?.VAT_number || '', [jsvat.germany]).isValid ||
        !guessed.supplier?.steuernummer) &&
      !!guessed.items?.some((item) => item.vat_regime?.vat_regime === ExpenseVATRegime.foreign_vat);

    const shouldShowVATRecoverabilityInfo = isGermanVATGuessedAsNotRecoverable && masterVATRegime === ExpenseVATRegime.foreign_vat;

    const shouldShowImportVATTicketBEWarning = isImportVATForm && isCustomerBE && isCustomerSubjectToVAT && !isInvoiceField.value;

    const isEuroExpense = formValues.currency === CurrenciesEnum.EUR;

    const shouldShowPendingStatusWarning = paymentStatusField.value === ExpensePaymentStatusType.pending;

    const paymentDate = formValues.payments.status?.payment_date;

    const does10DaysRuleApplies = (() => {
      if (!paymentDate) return false;
      const hasVATPaymentsCategory = formValues.items?.some((item) => item.category?.id === CATEGORY_TAXES_AND_INSURANCE_VAT_PAYMENT);
      if (!hasVATPaymentsCategory) return false;
      const _date = dayjs(paymentDate);
      return _date.month() === 0 && _date.date() >= 1 && _date.date() <= 10;
    })();

    const shouldShow10DaysRuleWarning = does10DaysRuleApplies && !!formValues.period && formValues.period.year >= dayjs(paymentDate).year();

    return {
      fields: {
        isInvoice: isInvoiceField,
        period: periodField,
        taxPeriod: taxPeriodField,
        supplier: supplierField,
        transactions: transactionsField,
        currency: currencyField,
        currencyRate: currencyRateField,
        paymentDate: paymentDateField,
        dueDate: dueDateField,
        paidByCash: paidByCashField,
        paidByOtherAccounts: paidByOtherAccountsField,
        paymentStatus: paymentStatusField,
        isSuggestedTransactionActive: isSuggestedTransactionActiveField,
      },
      helpers: {
        masterVATRegime,
        mixableVATRegimes,
        hasManyItemsWithMasterVATRegime,
        isCustomerSubjectToVAT,
        isDeductible,
        hasAnyServiceItems,
        isCustomerBE,
        isCustomerDE,
        isCustomerOnePersonCompany,
        shouldShowCalcRows,
        shouldShowNewItemBtn,
        isGermanPrivateCar,
        isImportVATForm,
        isCustomForm,
        hasLinkedPayments,
        isEuroExpense,
        isPaid,
        isUnpaid,
        isPending,
        isCreditNote,
        does10DaysRuleApplies,
        // warnings
        shouldShowTenDayRuleWarning,
        shouldShowIsInvoiceWarning,
        shouShowIsTicketWarning,
        shouldShowExpenseDateWarning,
        shouldShowPaymentDateWarning,
        // shouldShowExpenseDateHelper,
        isCustomerNaturalPerson,
        shouldShowTaxSupplierWarningForCompany,
        shouldShowTaxSupplierWarningForNaturalPerson,
        isGermanVATGuessedAsNotRecoverable,
        shouldShowVATRecoverabilityInfo,
        shouldShowImportVATTicketBEWarning,
        shouldShowPendingStatusWarning,
        shouldShow10DaysRuleWarning,
      },
    };
  };

  const getItemMeta = (formValues: IExpenseFormValuesV2, item: IExpenseFormValuesV2['items'][number], index: number, prefix = '') => {
    const {
      fields: { isInvoice },
      helpers: {
        masterVATRegime,
        isGermanVATGuessedAsNotRecoverable,
        isCustomerSubjectToVAT,
        isDeductible,
        isCustomerDE,
        shouldShowVATRecoverabilityInfo,
        isImportVATForm,
        isCustomForm,
        isCustomerBE,
      },
    } = getFormMeta(formValues);

    const isGermanPrivateCarItem = item.category?.id === SELF_EMPLOYED_CAR_PRIVATE_USE;

    const itemType = item.category?.type || null;
    const isItemService = itemType === ExpenseCategoryType.SERVICE;
    const isItemGood = itemType === ExpenseCategoryType.GOOD;

    const isFirstItem = index === 0;

    const isImportVATItem = item.category?.id === importVATCategory;
    const isImportFeesItem = item.category?.id === importFeesCategory;

    const itemGuessedData = formValues.guessed_data?.items?.[index];

    const categoryField = getFieldState({
      name: prefix + 'category',
      value: () => item.category,
      isHidden: isImportVATForm && !isFirstItem,
      suggestedChanges: ({ name }) => {
        if (isImportVATForm && !isFirstItem) {
          return [{ name, value: importFeesCategoryPlaceholder }];
        }
      },
    });

    const amortizationPeriodField = getFieldState({
      name: prefix + 'amortizationPeriod',
      value: () => item.amortizationPeriod,
      isApplicable: isAmortizationFn(item, formValues.currencyRate),
    });

    const isAssetField = getFieldState({
      name: prefix + 'isAsset',
      value: () => item.isAsset,
      isApplicable: !!amortizationPeriodField.value,
      suggestedChanges: ({ isApplicable, name }) => {
        if (!isApplicable) return [{ name, value: false }];
        return [{ name, value: true }];
      },
    });

    const nameField = getFieldState({
      name: prefix + 'name',
      value: () => item.name,
      isApplicable: !!isAssetField.value,
    });

    const vehicleField = getFieldState({
      name: prefix + 'vehicle',
      value: () => item.vehicle,
      isApplicable: isCustomerBE && vehicleCategoryIds.includes(item.category?.id),
      suggestedChanges: ({ isApplicable, name }) => {
        if (!isApplicable) return [{ name, value: null }];
      },
    });

    const categoryDoesNotAllowVAT = item.category?.allowsVAT === false;

    const isExclVATField = getFieldState({
      name: prefix + 'isExclVAT',
      value: () => item.isExclVAT,
      isApplicable: !!item.VATRate,
    });

    const maxDeductibleVATField = getFieldState({
      name: prefix + 'maxDeductibleVAT',
      value: () => item.maxDeductibleVAT,
      isHidden: isWeb,
      // (() => {
      //   if (isWeb) return true;
      //   return !(isInvoice.value || isItemGood || item.VATRate === 0);
      // })(),
      suggestedChanges: ({ name }) => {
        if (item.maxDeductibleVAT == null) {
          const suggested = item.category?.maxDeductibleVAT;
          if (suggested != null) return [{ name, value: suggested }];
        }
      },
    });

    const incomeTaxDeductibilityField = getFieldState({
      name: prefix + 'incomeTaxDeductibility',
      value: () => item.incomeTaxDeductibility,
      isHidden: isWeb,
      suggestedChanges: ({ name }) => {
        if (item.incomeTaxDeductibility == null) {
          const suggested = item.category?.incomeTaxDeductibility;
          if (suggested != null) return [{ name, value: suggested }];
        }
      },
    });

    const professionalPartField = getFieldState({
      name: prefix + 'professionalPart',
      value: () => item.professionalPart,
      isHidden: isDeductible || isGermanPrivateCarItem,
      suggestedChanges: ({ name }) => {
        if (isDeductible || isGermanPrivateCarItem || (isCustomerOnePersonCompany && item.professionalPart == null)) {
          return [{ name, value: 1 }];
        }
      },
    });

    const allowedVATRates = getAllowedVATRates(formValues.expenseDate);

    const {
      allowed: allowedVATRegimes,
      disallowed: disallowedVATRegimes,
      hidden: hiddenVATRegimes,
    } = getVATRegimesStatus({
      vatRate: item.VATRate,
      isInvoice: isInvoice.value,
      isSubjectToVAT: isCustomerSubjectToVAT,
      categoryType: item.category?.type,
      doesCategoryAllowVAT: item.category?.allowsVAT,
      isCustomForm,
      categoryId: item.category?.id,
      country: customerCountry,
    });

    const VATRateField = getFieldState({
      name: prefix + 'VATRate',
      value: () => item.VATRate,
      isHidden: isDeductible || isGermanPrivateCarItem || categoryDoesNotAllowVAT,
      suggestedChanges: (self) => {
        const { name } = self;
        const value = self.value(self);

        if (isDeductible || isGermanPrivateCarItem || categoryDoesNotAllowVAT) {
          return [{ name, value: 0 }];
        }

        if (value != null && !allowedVATRates.includes(value)) return [{ name, value: null }];

        if (value && item.VATRegime !== ExpenseVATRegime.standard) {
          const suggestedRegime = ExpenseVATRegime.standard;
          if (!allowedVATRegimes.includes(suggestedRegime)) return [{ name, value: null }];
          if (masterVATRegime && suggestedRegime !== masterVATRegime) return [{ name, value: null }];
        }
      },
    });

    const guessedVATRegime = itemGuessedData?.vat_regime?.vat_regime;

    const VATRegimeField = getFieldState({
      name: prefix + 'VATRegime',
      value: () => item.VATRegime,
      isHidden: item.VATRate !== 0 || isDeductible || isGermanPrivateCarItem || categoryDoesNotAllowVAT,
      suggestedChanges: (self) => {
        const { name } = self;
        const value = self.value(self);

        if (isDeductible || isGermanPrivateCarItem || categoryDoesNotAllowVAT) {
          return [{ name, value: ExpenseVATRegime.exempt_item }];
        }

        if (value) {
          if (!allowedVATRegimes.includes(value)) return [{ name, value: null }];
          if (!masterVATRegime || mixableVATRegimes.includes(value)) return;
          if (masterVATRegime && value !== masterVATRegime) return [{ name, value: null }];
        }

        if (!value) {
          if (isImportVATForm && importFeesCategory) {
            const suggested = ExpenseVATRegime.exempt_item;
            if (!allowedVATRegimes.includes(suggested)) return;
            return [{ name, value: suggested }];
          }
          if (guessedVATRegime) {
            const suggested = guessedVATRegime;
            if (!allowedVATRegimes.includes(suggested)) return;
            if (!mixableVATRegimes.includes(suggested) && masterVATRegime && suggested !== masterVATRegime) return;
            return [{ name, value: suggested }];
          }
          const suggested = ExpenseVATRegime.standard;
          if (!allowedVATRegimes.includes(suggested)) return;
          if (masterVATRegime && suggested !== masterVATRegime) return;
          return [{ name, value: suggested }];
        }
      },
    });

    const localVATRateField = getFieldState({
      name: prefix + 'localVATRate',
      value: () => item.localVATRate,
      isApplicable:
        item.VATRegime === ExpenseVATRegime.eu_reverse_charge ||
        item.VATRegime === ExpenseVATRegime.local_reverse_charge ||
        (item.VATRegime === ExpenseVATRegime.extra_eu_reverse_charge && isItemService),
      suggestedChanges: (self) => {
        const { isApplicable, name } = self;
        if (!isApplicable) return;
        const value = self.value(self);

        if (value != null && !allowedVATRates.includes(value)) {
          return [{ name, value: null }];
        }

        if (value == null) {
          const suggested = item.category?.suggestedVATRate;
          if (suggested != null && allowedVATRates.includes(suggested)) return [{ name, value: suggested }];
          return [{ name, value: maxVATRate }]; // maxVATRate is safe
        }
      },
    });

    const tripTypeField = getFieldState({
      name: prefix + 'tripType',
      value: () => item.tripType,
      isApplicable: isGermanPrivateCarItem,
    });

    const tripVehicleTypeField = getFieldState({
      name: prefix + 'tripVehicleType',
      value: () => item.tripVehicleType,
      isApplicable: tripTypeField.value === TripType.businessTrip,
    });

    const tripTotalKmField = getFieldState({
      name: prefix + 'tripTotalKm',
      value: () => item.tripTotalKm,
      isApplicable: tripTypeField.value != null,
    });

    const tripWorkingDaysField = getFieldState({
      name: prefix + 'tripWorkingDays',
      value: () => item.tripWorkingDays,
      isApplicable: tripTypeField.value === TripType.driveToWork,
    });

    const deductionField = getFieldState({
      name: prefix + 'deduction',
      value: () => item.deduction,
      isApplicable: isGermanPrivateCarItem,
      suggestedChanges: ({ isApplicable, name }) => {
        if (!isApplicable) return;

        if (tripTotalKmField.value != null) {
          const tripTotalKmValue = Math.floor(tripTotalKmField.value);
          if (tripTypeField.value === TripType.businessTrip) {
            if (tripVehicleTypeField.value === TripVehicleType.car) {
              return [{ name, value: round(tripTotalKmValue * 0.3) }];
            }
            if (tripVehicleTypeField.value === TripVehicleType.motorbike) {
              return [{ name, value: round(tripTotalKmValue * 0.2) }];
            }
          }

          if (tripTypeField.value === TripType.driveToWork) {
            if (tripWorkingDaysField.value != null) {
              return [
                {
                  name,
                  value: round(
                    (tripTotalKmField.value > 20 ? (tripTotalKmValue - 20) * 0.38 + 20 * 0.3 : tripTotalKmValue * 0.3) * Math.floor(tripWorkingDaysField.value),
                  ),
                },
              ];
            }
          }
        }

        return [{ name, value: undefined }];
      },
    });

    const currencyAmountField = getFieldState({
      name: prefix + 'currencyAmount',
      value: () => item.currencyAmount,
      isHidden: isGermanPrivateCarItem,
      suggestedChanges: ({ name }) => {
        if (isGermanPrivateCarItem) return [{ name, value: deductionField.value }];
      },
    });

    const shouldShowLunchWarning = item.category?.id === CATEGORY_LUNCH_VOUCHERS || item.category?.id === CATEGORY_LUNCH_VOUCHERS_ADMIN;

    const shouldShowUtilityVehicleWarning = isUtilityVehicle(item.category?.id);

    const shouldShowVehicleFineWarning = isVehicleFineCategory(item.category?.id);
    const shouldShowHealthWarning = item.category?.id === CATEGORY_HEALTH_INSURANCE;

    const shouldShowreverseChargeZeroRateWarning =
      item.VATRegime === ExpenseVATRegime.local_reverse_charge && item.localVATRate === 0 && (!item.category || !flightsCategories.includes(item.category.id));

    const shouldShowProfessionalDeductibilityWarning =
      item.professionalPart != null &&
      item.incomeTaxDeductibility != null &&
      item.professionalPart < 1 &&
      item.professionalPart === item.incomeTaxDeductibility;

    const shouldShowAmortizationStartDateWarning =
      !!formValues.expenseDate && !!item.amortizationPeriod && !!customer?.startDate && dayjs(formValues.expenseDate).isBefore(dayjs(customer.startDate));

    const shouldShowProfessionalPartDifferenceWarning =
      !professionalPartField.isHidden &&
      isCustomerNaturalPerson &&
      item.professionalPart != null &&
      item.category?.recommendedProfessionalPart != null &&
      item.professionalPart !== item.category.recommendedProfessionalPart;

    const shouldShowBankChargesProfessionalPartWarning =
      !professionalPartField.isHidden &&
      isCustomerNaturalPerson &&
      (item.category?.id === CATEGORY_BANK_CHARGES || item.category?.id === CATEGORY_INTEREST_AND_BANK_CHARGES) &&
      item.professionalPart != null &&
      item.professionalPart !== 1;

    const shouldShowWorkFromHomeProfessionalPartWarning =
      !professionalPartField.isHidden && isCustomerNaturalPerson && !!item.category && workFromHomeCategories.includes(item.category.id);

    const shouldShowCompanyProfessionalPartWarning =
      !professionalPartField.isHidden && isCustomerOnePersonCompany && item.professionalPart != null && item.professionalPart !== 1;

    const shouldShowDeductionBusinessTripHelper = tripTypeField.value === TripType.businessTrip;

    const shouldShowDeductionDriveToWorkHelper = tripTypeField.value === TripType.driveToWork;

    const shouldShowDeductionDestinationHelpers = deductionField.isApplicable && tripTypeField.value == null;

    const shouldShowBusinessCarInfo = isBusinessCar(item.category?.id);

    const shouldShowLumpsumWarning = item.category?.id === CATEGORY_WORKPLACE_RENT_HOMELUMPSUM;

    const shouldShowCompanyMealVoucherWarning =
      isCustomerOnePersonCompany && item.category?.id === CATEGORY_LUNCH_VOUCHERS && item.currencyAmount != null && !isMultipleOf2(item.currencyAmount);

    const shouldShowSoftwareInvoiceWarning = item.category?.id === CATEGORY_IT_SERVICE;

    const shouldShowSelfEmployedCompensationWarning =
      !isCustomerOnePersonCompany &&
      (item.category?.id === CATEGORY_DIRECTORS_COMPENSATION ||
        item.category?.id === CATEGORY_WORKERS_COMPENSATION ||
        item.category?.id === CATEGORY_OTHER_PERSONNEL_FEES);

    const shouldShowProfPartRentSecWorkplaceWarning = item.category?.id === CATEGORY_WORKPLACE_RENT_HOME_OFFICE_SECONDARY;

    const shouldShowCompensationWarning = item.category?.id === CATEGORY_DIRECTORS_COMPENSATION || item.category?.id === CATEGORY_WORKERS_COMPENSATION;

    const shouldShowLocalTransportWarning = item.category?.id === CATEGORY_TRAVEL_LOCAL_PUBLIC_TRANSPORTATION;

    const isVehicleLeasingCategory = !!item.category && vehiclesMonthlyLeasingCategories.includes(item.category?.id);

    const shouldShowVehiclesMonthlyLeasingCategoryFieldWarning = isVehicleLeasingCategory;

    const shouldShowVehiclesMonthlyLeasingAmountFieldWarning = isVehicleLeasingCategory && item.currencyAmount != null && item.currencyAmount >= 2000;

    // Belgian VAT will be declared, but since you can fully recover it, nothing extra needs to be paid.
    const shouldShowIntraReverseBESubject100ProWarning =
      isCustomerBE && isCustomerSubjectToVAT && item.professionalPart === 1 && item.VATRegime === ExpenseVATRegime.eu_reverse_charge;

    // You will need to pay and declare VAT in a special submission, available in your tax list.
    const shouldShowIntraReverseBEFranshiseeWarning = isCustomerBE && !isCustomerSubjectToVAT && item.VATRegime === ExpenseVATRegime.eu_reverse_charge;

    // Belgian VAT will be declared and you can immediately recover part of it.
    const shouldShowIntraReverseBESubjectPartProWarning =
      isCustomerBE &&
      isCustomerSubjectToVAT &&
      item.professionalPart != null &&
      item.professionalPart < 1 &&
      item.VATRegime === ExpenseVATRegime.eu_reverse_charge;

    // Belgian VAT will be declared, but since you can fully recover it, nothing extra needs to be paid.
    const shouldShowLocalReverseBEWarning = isCustomerBE && item.VATRegime === ExpenseVATRegime.local_reverse_charge;

    // Belgian VAT will be declared, but since you can fully recover it, nothing extra needs to be paid.
    const shouldShowExrtaServiceBESubjectWarning =
      isCustomerBE && isCustomerSubjectToVAT && isItemService && item.VATRegime === ExpenseVATRegime.extra_eu_reverse_charge;

    // You will need to pay VAT based on the expected VAT rate in Belgium. You will need to do a special VAT declaration, which will be prepared in your Accountable tax list.
    const shouldShowExrtaServiceBEFranshiseeWarning =
      isCustomerBE && !isCustomerSubjectToVAT && isItemService && item.VATRegime === ExpenseVATRegime.extra_eu_reverse_charge;

    // German VAT will be declared, but since you can fully recover it, nothing extra needs to be paid.
    const shouldShowIntraReverseDESubject100ProWarning =
      isCustomerDE && isCustomerSubjectToVAT && item.professionalPart === 1 && item.VATRegime === ExpenseVATRegime.eu_reverse_charge;

    // You will need to pay and declare VAT in a VAT return. Accountable does not support this. Reach out to our tax coaches.
    const shouldShowIntraReverseDEFranshiseeWarning = isCustomerDE && !isCustomerSubjectToVAT && item.VATRegime === ExpenseVATRegime.eu_reverse_charge;

    // German VAT will be declared and you can immediately recover part of it.
    const shouldShowIntraReverseDESubjectPartProWarning =
      isCustomerDE &&
      isCustomerSubjectToVAT &&
      item.professionalPart != null &&
      item.professionalPart < 1 &&
      item.VATRegime === ExpenseVATRegime.eu_reverse_charge;

    // VAT will be declared, but since you can fully recover it, nothing extra needs to be paid.
    const shouldShowLocalReverseDEWarning = isCustomerDE && item.VATRegime === ExpenseVATRegime.local_reverse_charge;

    // German VAT will be declared, but since you can fully recover it, nothing extra needs to be paid.
    const shouldShowExtraServiceDEWarning =
      isCustomerDE && !isCustomerSubjectToVAT && isItemService && item.VATRegime === ExpenseVATRegime.extra_eu_reverse_charge;

    // You can't recover foreign VAT through your normal VAT return
    const shouldShowOtherVATBEWarning = isCustomerBE && isCustomerSubjectToVAT && item.VATRegime === ExpenseVATRegime.foreign_vat;

    // You can't recover foreign VAT
    const shouldShowOtherVATDEWarning =
      !shouldShowVATRecoverabilityInfo && isCustomerDE && isCustomerSubjectToVAT && item.VATRegime === ExpenseVATRegime.foreign_vat;

    const shouldShowVATRecoverabilityConfirmationWarning = isGermanVATGuessedAsNotRecoverable && item.VATRegime === ExpenseVATRegime.standard;

    const shouldShowImportVATCategoryInfo = isImportVATForm && isImportVATItem;

    const shouldShowImportVATAmountInfo = isImportVATForm && isImportVATItem && isCustomerSubjectToVAT;

    const shouldShowVATDeductOnRentWarning = isCustomerSubjectToVAT && isInvoice.value && item.category?.id === CATEGORY_RENT;

    const shouldShowRenovationAmortInfo = item.category?.id === CATEGORY_WORKPLACE_RENOVATION && amortizationPeriodField.isApplicable;

    const shouldShowExtraGoodImportInfo = isItemGood && isCustomerSubjectToVAT && item.VATRegime === ExpenseVATRegime.extra_eu_reverse_charge;

    const shouldShowVATAmountNonStandardWarning = !!formValues._id && item.VATRate == null && item.VATRegime == null && item.VATAmount != null;

    const shouldShowPrivateInsuranceWarning = item.category?.id === CATEGORY_TAXES_AND_INSURANCE_PRIVATE_INSURANCE;
    const shouldShowMaxDeductibleVATVehicleInfoBox = isExpert && isCustomerBE && isCustomerSubjectToVAT && vehicleCategoryIds.includes(item.category?.id);

    return {
      fields: {
        localVATRate: localVATRateField,
        amortizationPeriod: amortizationPeriodField,
        isAsset: isAssetField,
        name: nameField,
        vehicle: vehicleField,
        currencyAmount: currencyAmountField,
        isExclVAT: isExclVATField,
        maxDeductibleVAT: maxDeductibleVATField,
        incomeTaxDeductibility: incomeTaxDeductibilityField,
        professionalPart: professionalPartField,
        VATRegime: VATRegimeField,
        VATRate: VATRateField,
        deduction: deductionField,
        tripType: tripTypeField,
        tripVehicleType: tripVehicleTypeField,
        tripTotalKm: tripTotalKmField,
        tripWorkingDays: tripWorkingDaysField,
        category: categoryField,
      },
      helpers: {
        allowedVATRegimes,
        disallowedVATRegimes,
        hiddenVATRegimes,
        isItemService,
        isItemGood,
        isImportVATItem,
        isImportFeesItem,
        // warnings
        shouldShowHealthWarning,
        shouldShowreverseChargeZeroRateWarning,
        shouldShowProfessionalDeductibilityWarning,
        shouldShowLunchWarning,
        shouldShowUtilityVehicleWarning,
        shouldShowVehicleFineWarning,
        shouldShowAmortizationStartDateWarning,
        shouldShowProfessionalPartDifferenceWarning,
        shouldShowBankChargesProfessionalPartWarning,
        shouldShowWorkFromHomeProfessionalPartWarning,
        shouldShowCompanyProfessionalPartWarning,
        shouldShowDeductionBusinessTripHelper,
        shouldShowDeductionDriveToWorkHelper,
        shouldShowDeductionDestinationHelpers,
        shouldShowBusinessCarInfo,
        shouldShowLumpsumWarning,
        shouldShowCompanyMealVoucherWarning,
        shouldShowSoftwareInvoiceWarning,
        shouldShowSelfEmployedCompensationWarning,
        shouldShowProfPartRentSecWorkplaceWarning,
        shouldShowCompensationWarning,
        shouldShowLocalTransportWarning,
        shouldShowVehiclesMonthlyLeasingCategoryFieldWarning,
        shouldShowVehiclesMonthlyLeasingAmountFieldWarning,
        shouldShowIntraReverseBESubject100ProWarning,
        shouldShowIntraReverseBEFranshiseeWarning,
        shouldShowIntraReverseBESubjectPartProWarning,
        shouldShowLocalReverseBEWarning,
        shouldShowExrtaServiceBESubjectWarning,
        shouldShowExrtaServiceBEFranshiseeWarning,
        shouldShowIntraReverseDESubject100ProWarning,
        shouldShowIntraReverseDEFranshiseeWarning,
        shouldShowIntraReverseDESubjectPartProWarning,
        shouldShowLocalReverseDEWarning,
        shouldShowExtraServiceDEWarning,
        shouldShowOtherVATBEWarning,
        shouldShowOtherVATDEWarning,
        shouldShowVATRecoverabilityConfirmationWarning,
        shouldShowImportVATCategoryInfo,
        shouldShowImportVATAmountInfo,
        shouldShowVATDeductOnRentWarning,
        shouldShowExtraGoodImportInfo,
        shouldShowVATAmountNonStandardWarning,
        shouldShowPrivateInsuranceWarning,
        shouldShowMaxDeductibleVATVehicleInfoBox,
        shouldShowRenovationAmortInfo,
      },
    };
  };

  const getSuggestedChanges = (formValues: IExpenseFormValuesV2) => {
    return [
      ...Object.values(getFormMeta(formValues).fields),
      ...(formValues.items || []).flatMap((item, i) => Object.values(getItemMeta(formValues, item, i, `items.${i}.`).fields)),
    ].flatMap((field) => field.suggestedChanges);
  };

  return {
    getFormMeta,
    getItemMeta,
    getSuggestedChanges,
  };
};

export default useFormMeta;
